import React from "react";
import styled from "styled-components";

import {
  Box,
  Button,
  Card,
  Columns,
  Column,
  Hidden,
  Heading,
  Section,
  Stack,
  Strong,
  Text,
} from "@origin-digital/ods-core";

import { cloudinary } from "src/consts";

const Image = styled<{
  mobileHeight?: string;
  size: "small" | "large";
  hero: IAssets["hero"];
}>(Box)`
  ${({ size, hero }) => `background-image: url(${cloudinary + hero[size]});`}
  ${({ size, mobileHeight }) =>
    size === "small" && `height: ${mobileHeight};`}

  background-size: cover;
  background-position: center center;
`;

export const Content: React.FC<{
  fullWidth?: boolean;
  stack?: boolean;
  content: IContent;
}> = ({ fullWidth, stack, content }) => (
  <Stack space="large">
    <Heading variant="h2" weight="medium" align={{ xs: "center", md: "left" }}>
      <Strong>{content.heading}</Strong>
    </Heading>
    {Array.isArray(content.byline) ? (
      content.byline.map((line, index) => (
        <Text key={`byline-${index}`} align={{ xs: "center", md: "left" }}>
          {line}
        </Text>
      ))
    ) : (
      <Text align={{ xs: "center", md: "left" }}>{content.byline}</Text>
    )}

    {content.cta ? (
      stack ? (
        <Stack space="medium" alignX="center">
          <Button href={content.cta.primary.location} fullWidth={fullWidth}>
            {content.cta.primary.text}
          </Button>
          {content.cta.secondary ? (
            <Button
              href={content.cta.secondary.location}
              variant="outlined"
              fullWidth={fullWidth}
            >
              {content.cta.secondary.text}
            </Button>
          ) : null}
        </Stack>
      ) : (
        <Columns space="medium">
          <Column width="content">
            <Button href={content.cta.primary.location}>
              {content.cta.primary.text}
            </Button>
          </Column>
          <Column width="content">
            {content.cta.secondary ? (
              <Button href={content.cta.secondary.location} variant="outlined">
                {content.cta.secondary.text}
              </Button>
            ) : null}
          </Column>
        </Columns>
      )
    ) : null}
  </Stack>
);

export interface IContent {
  heading: string;
  byline: string | string[];
  cta?: {
    primary: {
      text: string;
      location: string;
    };
    secondary?: {
      text: string;
      location: string;
    };
  };
}

export interface IAssets {
  hero: {
    small: string;
    large: string;
  };
}

export const HeroSection: React.FC<{
  fullWidth?: boolean;
  content: IContent;
  assets: IAssets;
}> = ({ fullWidth, content, assets }) => (
  <>
    {/* SM / MD / LG */}
    <Hidden below="md">
      <Image size="large" hero={assets.hero}>
        <Section backgroundColor="transparent">
          {/* SM */}
          <Hidden above="md">
            <Columns>
              <Column width="2/3">
                <Card>
                  <Content content={content} />
                </Card>
              </Column>
            </Columns>
          </Hidden>

          {/* MD */}
          <Hidden below="lg">
            <Hidden above="lg">
              <Columns>
                <Column width="1/2">
                  <Card>
                    <Content content={content} />
                  </Card>
                </Column>
              </Columns>
            </Hidden>
          </Hidden>

          {/* LG */}
          <Hidden below="xl">
            <Columns>
              <Column width="5/12">
                <Card>
                  <Content content={content} />
                </Card>
              </Column>
            </Columns>
          </Hidden>
        </Section>
      </Image>
    </Hidden>

    {/* XS */}
    <Hidden above="sm">
      <Image size="small" hero={assets.hero} mobileHeight="112px" />
      <Section>
        <Content content={content} stack fullWidth={fullWidth} />
      </Section>
    </Hidden>
  </>
);
